import { A } from '@adornis/base/env-info.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import type { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import type { ContextMenuItem } from '@chancenwerk/client/components/chancen-component-context-menu.js';
import '@chancenwerk/client/components/chancen-component-zip-city-picker.js';
import type { ChancenComponentZipCityPicker } from '@chancenwerk/client/components/chancen-component-zip-city-picker.js';
import '@chancenwerk/client/components/semantics/chancen-component-semantic-product-picker.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ZipCity } from '@chancenwerk/shared/adresse.js';
import { CapacityAPI } from '@chancenwerk/shared/capacity/api/CapacityAPI.js';
import { CapacityGuestDay } from '@chancenwerk/shared/capacity/entities/CapacityGuestDay.js';
import { CapacityRequestStatus } from '@chancenwerk/shared/capacity/entities/CapacityRequest.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-dropdown-search-selection.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import '@chancenwerk/client/design/components/cr-tabs.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { ProductAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/ProductAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { switchMap, tap } from 'rxjs';
import { KraefteDialogEditCapacity } from '../components/dialog/kraefte-dialog-edit-capacity.js';
import { globalKpfFilter } from '../globalSubjects.js';

/**
 * @customElement kraefte-page-capacity-management-overview
 */
@customElement('kraefte-page-capacity-management-overview')
export class KraeftePageCapacityManagementOverview extends CrPage {
  @state() private readonly _kpfFilter = new RXController(this, globalKpfFilter);
  @state() private _loading = false;
  @state() private readonly _reports = new RXController(
    this,
    this._kpfFilter.observable.pipe(
      switchMap(filter => {
        this._loading = true;
        return CapacityAPI.subscribeOverview(filter)({
          KPFID: 1,
          leko: 1,
          schuko: 1,
          highPriority: 1,
          notification: 1,
          kpf: { kuerzel: 1, school: { kuerzel: 1 }, produkt: { kuerzel: 1 } },
        });
      }),
      tap(() => (this._loading = false)),
    ),
  );

  constructor() {
    super();
    this.permission = ChancenPermission.kraefte.LOGIN_KRAEFTE;
    this.label = 'Bedarfsplanung';
  }

  override connectedCallback() {
    super.connectedCallback();

    const kpfIDs = JSON.parse(localStorage.getItem('kpfFilter') ?? '[]') as string[];
    if (kpfIDs.length === 0) {
      return;
    }
    this._kpfFilter.value.ids = kpfIDs;
    this._kpfFilter.next(this._kpfFilter.value);
  }

  override header() {
    return html`
      <chancen-component-context-menu
        .items=${[
          {
            icon: 'add',
            label: 'Bedarf anmelden',
            action: () => {
              KraefteDialogEditCapacity.showPopup<string>({
                modal: true,
                props: { hideOnboarding: true, status: CapacityRequestStatus.NEW },
              }).then(res => {
                if (res) {
                  this._kpfFilter.value._id = A.getGloballyUniqueID();
                  this._kpfFilter.next(this._kpfFilter.value);
                  this.requestUpdate();
                }
              });
            },
          },
        ] satisfies ContextMenuItem[]}
      ></chancen-component-context-menu>
    `;
  }

  override content() {
    return html`<cr-grid columns="500px 1fr" space="sm" padding="xs" ${css({ alignItems: 'start' })}>
      ${this._getFilterSection()} ${this._overview()}
    </cr-grid>`;
  }

  private _overview() {
    if (!this._reports.value || this._reports.isLoading || this._loading) {
      return html` <cr-card crossaxis-center padding="lg" space="lg">
        <cr-loader></cr-loader>
        <cr-text>Bedarfe werden geladen.</cr-text>
      </cr-card>`;
    }

    return html` ${this._capaTop()} `;
  }

  private _cityTags() {
    if ((this._kpfFilter.value.zipCities ?? []).length === 0) {
      return nothing;
    }
    return html` <cr-flex horizontal crossaxis-center space="md">
      ${repeat(
        this._kpfFilter.value.zipCities ?? [],
        z => z.city,
        z => {
          return html`<cr-flex
            horizontal
            crossaxis-center
            space="sm"
            ${css({
              borderRadius: this.sizes.borderRadiusSecondary,
              backgroundColor: this.colors.neutral.shade(50),
              padding: `${this.spacing.xs} ${this.spacing.md}`,
            })}
          >
            <cr-text>${z.city}</cr-text>
            <cr-button
              mode="icon"
              icon="delete"
              tone="error"
              @click=${() => {
                this._kpfFilter.value.zipCities = this._kpfFilter.value.zipCities?.filter(
                  zipCity => zipCity.city !== z.city,
                );
                this._kpfFilter.next(this._kpfFilter.value);
              }}
            ></cr-button>
          </cr-flex>`;
        },
      )}
    </cr-flex>`;
  }

  zipPickerRef: Ref<ChancenComponentZipCityPicker> = createRef();

  private _getFilterSection() {
    return html`
      <cr-card ${css({ width: '100%' })} padding="md" space="md">
        <cr-flex
          horizontal
          space-between
          ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: this.spacing.md })}
        >
          <cr-text bold>Suche</cr-text>
        </cr-flex>
        <cr-grid columns="1fr" space="md">
          <chancen-component-zip-city-picker
            ${ref(this.zipPickerRef)}
            flex
            only-cities
            clearable
            @value-picked=${(e: CustomEvent<{ value: ZipCity }>) => {
              if (!e.detail.value.city || e.detail.value.city?.length === 0) {
                return;
              }

              if (!this._kpfFilter.value.zipCities) {
                this._kpfFilter.value.zipCities = [];
              }

              this._kpfFilter.value.zipCities.push(new ZipCity(e.detail.value.toJSON()));

              // ? why is it emitting with ', Deutschland' here?
              // TODO: fix this
              const toFix = this._kpfFilter.value.zipCities[this._kpfFilter.value.zipCities.length - 1];
              if (toFix?.city?.includes(', ')) {
                toFix.city = toFix.city.split(', ')[0];
              }

              // * make sure that the city is unique
              this._kpfFilter.value.zipCities = [
                ...new Map(this._kpfFilter.value.zipCities.map(item => [item.city, item])).values(),
              ];

              if (this.zipPickerRef.value) {
                this.zipPickerRef.value.value.next(null);
              }
              this._kpfFilter.next(this._kpfFilter.value);
            }}
          >
          </chancen-component-zip-city-picker>
          <chancen-component-semantic-product-picker-multi
            @value-picked=${(e: ValueEvent<Maybe<Product[]>>) => {
              if (!e.detail.value) {
                this._kpfFilter.value.aspects = this._kpfFilter.value.aspects.filter(
                  a => a.customID !== 'produkt._class',
                );
              } else {
                this._kpfFilter.value.aspects = [
                  ...this._kpfFilter.value.aspects.filter(a => a.fieldName !== 'produkt._class'),
                  new ProductAspect({ fieldName: 'produkt._class', values: e.detail.value?.map(p => p._class) }),
                ];
              }
              this._kpfFilter.next(this._kpfFilter.value);
            }}
          >
          </chancen-component-semantic-product-picker-multi>
          <cr-grid columns="1fr max-content" space="md">
            <x-multi-entity-picker
              clearable
              placeholder="KPF"
              icon="school"
              .value=${this._kpfFilter.value.ids}
              .renderString=${(kpf: Maybe<KPF<Product>>) => {
                if (!kpf) return '';
                return kpf.toString();
              }}
              .renderSelected=${(kpfs: Maybe<KPF<Product>[]>) => {
                if (!kpfs) {
                  return '';
                }
                const txt = kpfs.map(k => k.toString()).join(', ');
                if (txt.length > 50) {
                  return txt.slice(0, 30) + '...';
                }
                return txt;
              }}
              .options=${{
                class: KPF,
                operation: KPFAPI.subscribeByFilter,
                selectionSet: {
                  name: 1,
                  kuerzel: 1,
                  produkt: { _class: 1, kuerzel: 1 },
                  school: { name: 1, kuerzel: 1 },
                },
                filter: new AdornisFilter({
                  aspects: [new BooleanAspect({ customID: 'active', value: true }), new ProductAspect({ values: [] })],
                }),
              }}
              @value-picked=${async (e: ValueEvent<Maybe<string[]>>) => {
                this._kpfFilter.value.ids = e.detail.value ?? [];
                this._kpfFilter.next(this._kpfFilter.value);
              }}
            >
            </x-multi-entity-picker>
            <chancen-component-context-menu
              ${css({
                maxWidth: '80px',
                minWidth: '0',
                backgroundColor: this.colors.neutral.shade(50),
                padding: `0 ${this.spacing.sm}`,
                borderRadius: this.sizes.borderRadiusSecondary,
              })}
              icon="save"
              .items=${[
                {
                  icon: 'add',
                  label: 'KPFs speichern',
                  action: async () => {
                    localStorage.setItem('kpfFilter', JSON.stringify(this._kpfFilter.value.ids));
                    await ChromiumSnackbar.show('KPFs gespeichert');
                  },
                },
                {
                  icon: 'add',
                  label: 'KPFs laden',
                  action: async () => {
                    const kpfIDs = JSON.parse(localStorage.getItem('kpfFilter') ?? '[]') as string[];
                    if (kpfIDs.length === 0) {
                      await ChromiumSnackbar.show('Keine KPFs gespeichert');
                      return;
                    }

                    await ChromiumDialog.guard(async () => {
                      this._kpfFilter.value.ids = kpfIDs;
                      this._kpfFilter.next(this._kpfFilter.value);
                    });
                  },
                },
              ]}
            >
            </chancen-component-context-menu>
          </cr-grid>
          ${this._cityTags()}
        </cr-grid>
      </cr-card>
    `;
  }

  private _title(left: Renderable, right?: Renderable) {
    return html` <cr-flex
      horizontal
      space-between
      ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: this.spacing.md })}
    >
      <cr-text bold>${left}</cr-text>
      <cr-text bold>${right}</cr-text>
    </cr-flex>`;
  }

  private _capaTop() {
    const headerStyle = { padding: this.spacing.sm, backgroundColor: 'rgba(0,0,0,0.1)' };
    const alternateRowStyle = (index: number, highPriority: boolean) => ({
      padding: this.spacing.sm,
      backgroundColor: highPriority ? this.colors.tone.error.shade(200) : index % 2 ? 'rgba(0,0,0,0.05)' : '',
    });

    return html`<cr-flex space="md">
      <cr-card padding="md" space="md">
        ${this._title('Schnuppertage Heute')}

        <cr-data-view
          .options=${{
            class: CapacityGuestDay,
            view: new View({
              filter: new AdornisFilter({
                aspects: [
                  ...((this._kpfFilter.value?.ids?.length ?? 0) > 0
                    ? [
                        new StringAspect({
                          fieldName: 'KPFID',
                          mode: 'containsOR',
                          value: this._kpfFilter.value.ids?.join(',') ?? '',
                        }),
                      ]
                    : []),
                  new DateTimeAspect({
                    fieldName: 'day',
                    mode: 'range',
                    firstValue: DateTime.now().startOf('day').toJSDate(),
                    secondValue: DateTime.now().endOf('day').toJSDate(),
                  }),
                ],
              }),
            }),
            isEntryClickable: false,
            defaultFieldMapping: {
              DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy'),
              boolean: (value: boolean) => (value ? 'ja' : 'nein'),
            },
            onlyShowLastTranslation: true,
            selectionSet: { ...CapacityGuestDay.allFields },
            customColumns: [
              { fieldName: 'day' },
              { fieldName: 'vorname' },
              { fieldName: 'nachname' },
              { fieldName: 'telefonBewerber' },
              {
                fieldName: 'bewerbungslink',
                getFieldMapping: e =>
                  e.bewerbungslink
                    ? html`<cr-text>
                        <cr-link href=${e.bewerbungslink} target="_blank" @click=${e => e.stopPropagation()}>
                          Bewerbungslink
                        </cr-link>
                      </cr-text>`
                    : nothing,
              },
              {
                fieldName: 'KPF',
                selectionSet: { KPF: { produkt: { _class: 1, kuerzel: 1 }, school: { name: 1, kuerzel: 1 } } },
                getFieldMapping: e => e.KPF?.kuerzel,
              },
            ],
          } satisfies DataViewOptions<typeof CapacityGuestDay>}
        ></cr-data-view>
      </cr-card>
      <cr-card padding="md" space="md">
        ${this._title('offene Stellen')}
        <cr-grid columns="max-content 1fr 1fr">
          <cr-text ${css({ ...headerStyle })} bold>Kürzel</cr-text>
          <cr-text ${css({ ...headerStyle, textAlign: 'center' })} bold>LeKo</cr-text>
          <cr-text ${css({ ...headerStyle, textAlign: 'center' })} bold>SchuKo</cr-text>
          ${repeat(
            this._reports.value ?? [],
            r => r.KPFID,
            (r, i) => {
              const rowStyle = alternateRowStyle(i, !!r.highPriority);
              return html`
                <cr-link
                  ${css(rowStyle)}
                  ${acss({
                    position: 'relative',
                    color: 'black',
                    '&:hover': { color: this.colors.neutral.shade(500), cursor: 'pointer' },
                  })}
                  bold
                  href=${`/capacity/${r.KPFID}`}
                >
                  <cr-text>${r.kpf?.kuerzel}</cr-text>
                  <cr-icon
                    ${css({
                      top: '4px',
                      right: '-5px',
                      position: 'absolute',
                      display: r.notification ? 'block' : 'none',
                      color: this.colors.tone.error,
                    })}
                    >info</cr-icon
                  >
                </cr-link>
                <cr-text ${css({ ...rowStyle, textAlign: 'center' })}> ${r.leko} </cr-text>
                <cr-text ${css({ ...rowStyle, textAlign: 'center' })}> ${r.schuko} </cr-text>
              `;
            },
          )}
        </cr-grid>
      </cr-card>
    </cr-flex>`;
  }
}
